import { useEffect, useState } from 'react';
import { Check, Copy, Info } from 'lucide-react';
import type { Organization } from '../../../types/admin';
import {
  CardEditButton,
} from '../../admin/QuoteDefaultsPage';

// ── Types ────────────────────────────────────────────────────────────────

export interface OnlineLeadCaptureData {
  enabled: boolean;
  slug: string;
  welcomeMessage: string;
  enableLockInDate: boolean;
  enableRequestEstimate: boolean;
  enableShowPriceEstimate: boolean;
  /** Name of a card still in edit mode (its unsaved changes would be lost on Continue), or null. */
  openEditor: string | null;
}

interface OnlineLeadCaptureStepProps {
  org: Organization;
  onChange: (data: OnlineLeadCaptureData) => void;
}

const BOOKING_DOMAIN = 'booking.iomipro.com';

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
}

// ── Toggle ──────────────────────────────────────────────────────────────

function Toggle({ checked, onChange, disabled, label }: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative w-12 h-7 rounded-full transition-colors ${
        checked ? 'bg-plum' : 'bg-stone'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      aria-label={label}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

// ── Main component ───────────────────────────────────────────────────────

export function OnlineLeadCaptureStep({ org, onChange }: OnlineLeadCaptureStepProps) {
  const [enabled, setEnabled] = useState(org.featureOnlineLeadCapture === true);

  // Booking URL
  const [slug, setSlug] = useState(org.slug ?? slugify(org.name));
  const [slugEditing, setSlugEditing] = useState(false);
  const [editSlug, setEditSlug] = useState(slug);
  const [copied, setCopied] = useState(false);

  // Welcome Message
  const [welcomeMessage, setWelcomeMessage] = useState(
    org.bookingConfig?.welcomeMessage ?? 'Request a free estimate for your upcoming move!',
  );
  const [welcomeEditing, setWelcomeEditing] = useState(false);
  const [editWelcome, setEditWelcome] = useState(welcomeMessage);

  // Customer Actions
  const [lockInDate, setLockInDate] = useState(org.bookingConfig?.enableLockInDate ?? true);
  const [requestEstimate, setRequestEstimate] = useState(org.bookingConfig?.enableRequestEstimate ?? true);
  const [showPriceEstimate, setShowPriceEstimate] = useState(org.bookingConfig?.enableShowPriceEstimate ?? true);
  const [actionsEditing, setActionsEditing] = useState(false);
  const [draftActions, setDraftActions] = useState({ lockInDate, requestEstimate, showPriceEstimate });

  const bookingUrl = slug ? `${BOOKING_DOMAIN}/${slug}` : '';
  const openEditor = !enabled ? null
    : slugEditing ? 'Booking URL'
    : welcomeEditing ? 'Welcome Message'
    : actionsEditing ? 'Customer Actions'
    : null;

  useEffect(() => {
    onChange({
      enabled,
      slug,
      welcomeMessage,
      enableLockInDate: lockInDate,
      enableRequestEstimate: requestEstimate,
      enableShowPriceEstimate: showPriceEstimate,
      openEditor,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, slug, welcomeMessage, lockInDate, requestEstimate, showPriceEstimate, openEditor]);

  // Slug handlers
  function handleSlugSave() {
    setSlug(editSlug);
    setSlugEditing(false);
  }
  function handleSlugCancel() {
    setEditSlug(slug);
    setSlugEditing(false);
  }

  // Welcome handlers
  function handleWelcomeSave() {
    setWelcomeMessage(editWelcome);
    setWelcomeEditing(false);
  }
  function handleWelcomeCancel() {
    setEditWelcome(welcomeMessage);
    setWelcomeEditing(false);
  }

  // Actions handlers
  function handleActionsSave() {
    setLockInDate(draftActions.lockInDate);
    setRequestEstimate(draftActions.requestEstimate);
    setShowPriceEstimate(draftActions.showPriceEstimate);
    setActionsEditing(false);
  }
  function handleActionsCancel() {
    setDraftActions({ lockInDate, requestEstimate, showPriceEstimate });
    setActionsEditing(false);
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(`https://${bookingUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  }

  return (
    <div className="space-y-4">
      {/* ── Enable Toggle ── */}
      <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
        <div className="px-4 py-3.5 border-b border-stone">
          <p className="font-semibold text-small text-midnight">Lead Capture Settings</p>
          <p className="text-[12px] text-slate leading-snug mt-0.5">
            Customers can request estimates through a branded booking form.
          </p>
        </div>
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[13px] text-midnight font-medium">Lead Intake Form Enabled</p>
              <p className="text-[12px] text-slate mt-0.5">
                {enabled
                  ? 'Customers can access the booking form.'
                  : 'The booking form is currently disabled.'}
              </p>
            </div>
            <Toggle
              checked={enabled}
              onChange={setEnabled}
              label="Toggle lead intake form enabled"
            />
          </div>
        </div>
      </section>

      {enabled && (
        <>
          {/* ── Booking URL ── */}
          <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
            <div className="px-4 py-3.5 border-b border-stone flex items-center justify-between">
              <p className="font-semibold text-small text-midnight">Booking URL</p>
              <CardEditButton
                editing={slugEditing}
                onEdit={() => { setEditSlug(slug); setSlugEditing(true); }}
                onSave={handleSlugSave}
                onCancel={handleSlugCancel}
              />
            </div>
            <div className="px-4 py-4">
              {bookingUrl && (
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 bg-mist rounded-lg px-3 py-2.5 text-[12px] text-plum font-mono truncate">
                    https://{bookingUrl}
                  </div>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="shrink-0 w-10 h-10 flex items-center justify-center rounded-lg border border-stone hover:bg-mist transition-colors"
                    aria-label="Copy URL"
                  >
                    {copied ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} className="text-slate" />}
                  </button>
                </div>
              )}

              <div>
                <label className="text-[11px] font-bold uppercase tracking-widest text-slate block mb-1">
                  URL Slug
                </label>
                <div className="flex items-center border border-stone rounded-lg overflow-hidden">
                  <span className="pl-3 text-[12px] text-slate whitespace-nowrap">{BOOKING_DOMAIN}/</span>
                  <input
                    type="text"
                    value={slugEditing ? editSlug : slug}
                    onChange={e => setEditSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    disabled={!slugEditing}
                    className="flex-1 py-2.5 pr-3 text-small text-midnight bg-transparent focus:outline-none disabled:text-slate disabled:cursor-not-allowed"
                    placeholder="your-company"
                    maxLength={60}
                  />
                </div>
                <p className="text-[11px] text-slate mt-1">
                  Must be unique. Lowercase letters, numbers, and hyphens only.
                </p>
              </div>
            </div>
          </section>

          {/* ── Welcome Message ── */}
          <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
            <div className="px-4 py-3.5 border-b border-stone flex items-center justify-between">
              <p className="font-semibold text-small text-midnight">Welcome Message</p>
              <CardEditButton
                editing={welcomeEditing}
                onEdit={() => { setEditWelcome(welcomeMessage); setWelcomeEditing(true); }}
                onSave={handleWelcomeSave}
                onCancel={handleWelcomeCancel}
              />
            </div>
            <div className="px-4 py-4">
              <p className="text-[12px] text-slate mb-2">
                Optional text shown at the top of the booking form.
              </p>
              <textarea
                value={welcomeEditing ? editWelcome : welcomeMessage}
                onChange={e => setEditWelcome(e.target.value)}
                disabled={!welcomeEditing}
                rows={3}
                maxLength={500}
                className="w-full px-3 py-2 text-small text-midnight bg-white border border-stone rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-plum/20 resize-none
                           disabled:bg-mist disabled:cursor-not-allowed"
                placeholder="Get a free estimate for your upcoming move!"
              />
              <p className="text-[11px] text-slate mt-1 text-right">
                {(welcomeEditing ? editWelcome : welcomeMessage).length}/500
              </p>
            </div>
          </section>

          {/* ── Customer Actions ── */}
          <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
            <div className="px-4 py-3.5 border-b border-stone flex items-center justify-between">
              <p className="font-semibold text-small text-midnight">Customer Actions</p>
              <CardEditButton
                editing={actionsEditing}
                onEdit={() => { setDraftActions({ lockInDate, requestEstimate, showPriceEstimate }); setActionsEditing(true); }}
                onSave={handleActionsSave}
                onCancel={handleActionsCancel}
              />
            </div>
            <div className="px-4 py-4 space-y-4">
              {/* Show Price Estimate */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[13px] text-midnight font-medium">Show Price Estimate</p>
                  <p className="text-[12px] text-slate">Allow customers to view an estimate based on your configured settings.</p>
                </div>
                <Toggle
                  checked={actionsEditing ? draftActions.showPriceEstimate : showPriceEstimate}
                  onChange={v => setDraftActions(prev => ({ ...prev, showPriceEstimate: v }))}
                  disabled={!actionsEditing}
                  label="Toggle Show Price Estimate"
                />
              </div>

              {/* Rate Cards callout (no link — wizard context) */}
              <div className="bg-plum/5 border border-plum/20 rounded-lg p-3 flex items-start gap-2">
                <Info size={14} className="text-plum shrink-0 mt-0.5" />
                <p className="text-[12px] text-slate">
                  Estimate pricing is based on your org&apos;s rate configuration. You can update pricing in the Rate Cards page after completing setup.
                </p>
              </div>

              {/* Lock In Moving Date */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[13px] text-midnight font-medium">Lock In Moving Date</p>
                  <p className="text-[12px] text-slate">Requires a refundable deposit.</p>
                </div>
                <Toggle
                  checked={actionsEditing ? draftActions.lockInDate : lockInDate}
                  onChange={v => setDraftActions(prev => ({ ...prev, lockInDate: v }))}
                  disabled={!actionsEditing}
                  label="Toggle Lock In Moving Date"
                />
              </div>

              {/* Deposit callout (no link — wizard context) */}
              {(actionsEditing ? draftActions.lockInDate : lockInDate) && (
                <div className="bg-plum/5 border border-plum/20 rounded-lg p-3 flex items-start gap-2">
                  <Info size={14} className="text-plum shrink-0 mt-0.5" />
                  <p className="text-[12px] text-slate">
                    Deposit amount and payment schedule are configured in the Payments step of this wizard and in Customer Payments settings.
                  </p>
                </div>
              )}

              {/* Request a Free Estimate */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[13px] text-midnight font-medium">Request a Free Estimate</p>
                  <p className="text-[12px] text-slate">Customer requests someone reach out.</p>
                </div>
                <Toggle
                  checked={actionsEditing ? draftActions.requestEstimate : requestEstimate}
                  onChange={v => setDraftActions(prev => ({ ...prev, requestEstimate: v }))}
                  disabled={!actionsEditing}
                  label="Toggle Request a Free Estimate"
                />
              </div>
            </div>
          </section>

          {/* Preview hint */}
          <div className="flex items-start gap-3 px-3.5 py-3 rounded-xl bg-mist border border-stone">
            <Info size={16} className="shrink-0 text-slate mt-0.5" />
            <p className="text-[13px] text-slate leading-relaxed">
              Your booking form will use the brand color and logo you set in the Company Profile step.
              You can further customize the form, add custom discovery sources, and configure online
              quotes anytime in the Admin Panel.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
